import Modifier from 'ember-modifier';
import COLORS from '../../utils/colors';
import { registerDestructor } from '@ember/destroyable';
import { DateTime } from 'scaleflow/utils/date-time';
import '../../config/echarts_theme';

import * as echarts from 'echarts/core';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
} from 'echarts/components';

// Import bar charts, all suffixed with Chart
import { BarChart } from 'echarts/charts';
import { LineChart } from 'echarts/charts';

import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

import { service } from '@ember/service';

// Register the required components
echarts.use([
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  LegendComponent,
]);

function cleanup(instance) {
  instance?.chart?.dispose?.();
}

export default class CapacityTeamsModifier extends Modifier {
  @service intl;
  @service store;

  plannedInitiativesLabel = 'Plannable time';

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  async modify(element, [roadmap, initiatives, scenario], options) {
    this.initiatives = await initiatives;
    this.roadmap = await roadmap;
    this.scenario = await scenario;
    this.options = options;

    const promises = [];
    this.initiatives?.forEach((initiative) => {
      promises.push(this.store.findRecord('bon', initiative.id));
      // promises.push(initiative.belongsTo('bon').load());
      promises.push(initiative.hasMany('teams').load());
    });
    await Promise.allSettled(promises);
    this.setupChart(element).then(() => this.updateChart());
  }

  async setupChart(element) {
    if (this.chart != undefined) return;

    this.chart = echarts.init(element, 'producthub', {
      height: this.options.height ?? 512,
    });
  }

  updateChart() {
    const options = {
      ...this.chartOptions,
      series: this.series,
      dataset: this.dataset,
    };

    this.chart.setOption(options, { notMerge: true });
  }

  calculate_default_height() {
    return 150 + this.options.teams.length * 50;
  }

  get chartOptions() {
    let context = this;
    const options = {
      tooltip: {
        trigger: 'axis',
        position: function (point, params, dom, rect, size) {
          // Position tooltip to the right of cursor
          return [point[0] + 20, point[1] - size.contentSize[1] / 2];
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 15,
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
          color: '#333',
        },
        extraCssText:
          'box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15); border-radius: 8px;',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
        formatter: function (params) {
          const data = params[0].data;
          const teamName = params[0].axisValueLabel;
          const percentage = data.percentage;
          const workDetails = data.work_details;
          const capacityDetails = data.capacity_details;

          let output = `<h3>Team: <span class="font-semibold">${teamName}</span></h3>`;

          // Status based on thresholds
          let statusColor, statusText;
          if (!capacityDetails) {
            statusColor = 'gray';
            statusText = 'Unknown';
          } else {
            const totalWork = data.total_work;
            const initiativeCapacity = parseFloat(
              capacityDetails.initiative_capacity,
            );
            const totalCapacity = data.total_capacity;

            if (totalWork > totalCapacity) {
              statusColor = '#FF4136';
              statusText = 'Exceeding Total Capacity';
            } else if (totalWork > initiativeCapacity) {
              statusColor = '#FF851B';
              statusText = 'Using Flexible Capacity';
            } else {
              statusColor = '#2ECC40';
              statusText = 'Within Capacity';
            }
          }

          output += `<h3 class="font-semibold">Workload: <span class="text-right font-bold">${percentage}%</span></h3>`;
          output += `<h3 class="font-semibold">Status: <span style="color: ${statusColor}; font-weight: bold;">${statusText}</span></h3>`;

          // Add breakdown details
          if (workDetails && workDetails.initiatives) {
            // Sort initiatives by workload descending
            const sortedInitiatives = workDetails.initiatives.sort(
              (a, b) => b.workload - a.workload,
            );

            output += '<h3 class="font-semibold mt-2">Initiatives</h3>';
            output += '<table class="w-full">';

            sortedInitiatives.forEach((initiative) => {
              output += context._addWorkloadRow(
                initiative.name,
                initiative.workload,
              );
            });

            output += `<tr style="border-top: 1px solid #ddd;">
              <td class="pr-2">Planned Work</td>
              <td class="text-right font-bold tabular-nums">${Math.round(data.total_work, 1)} pw</td>
              <td></td>
            </tr>`;
            //output += context._addWorkloadRow('<span class="font-semibold">Planned Work</span>', data.total_work);
            output += '</table>';
          }

          // Add capacity information with thresholds
          if (capacityDetails) {
            output += '<h3 class="font-semibold mt-2">Capacity Thresholds</h3>';
            output += '<table class="w-full">';

            const initiativeCapacity = parseFloat(
              capacityDetails.initiative_capacity,
            );
            const flowCapacity = parseFloat(capacityDetails.flow_capacity);
            const totalCapacity = data.total_capacity;
            const totalWork = data.total_work;

            // Initiative capacity with total work percentage
            let totalWorkPercentage =
              initiativeCapacity > 0
                ? Math.round((totalWork / initiativeCapacity) * 100)
                : 0;

            output += `<tr>
              <td class="pr-2">${context.plannedInitiativesLabel}</td>
              <td class="text-right font-bold tabular-nums">${Math.round(initiativeCapacity * 100) / 100} pw</td>
              <td class="text-right font-bold" style="color: ${totalWorkPercentage > 100 ? '#FF4136' : '#2ECC40'}">
                (${totalWorkPercentage}%)
              </td>
            </tr>`;

            // Flow capacity without percentage
            output += `<tr>
              <td class="pr-2">Flexible</td>
              <td class="text-right font-bold tabular-nums">${Math.round(flowCapacity * 100) / 100} pw</td>
              <td></td>
            </tr>`;

            // Overflow indicator if exceeding total capacity
            let overflow = Math.max(0, totalWork - totalCapacity);
            if (overflow > 0) {
              output += `<tr>
                <td class="pr-2">Exceeding Capacity</td>
                <td class="text-right font-bold tabular-nums" style="color: #FF4136">${Math.round(overflow * 100) / 100} pw</td>
                <td class="text-right font-bold" style="color: #FF4136">
                  (${Math.round((overflow / totalCapacity) * 100)}% over)
                </td>
              </tr>`;
            }

            output += `<tr style="border-top: 1px solid #ddd;">
              <td class="pr-2">Total Capacity</td>
              <td class="text-right font-bold tabular-nums">${Math.round(totalCapacity * 100) / 100} pw</td>
              <td></td>
            </tr>`;

            output += '</table>';
          }

          return output;
        },
      },
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      grid: {
        left: '2%',
        right: '10%',
        bottom: '8%',
        top: '10%',
        containLabel: true,
      },
      legend: {
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Workload %',
        nameLocation: 'center',
        nameGap: 35,
        nameTextStyle: {
          fontFamily: 'Arial',
          fontSize: 13,
          fontWeight: 'bold',
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#ccc',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(220, 220, 220, 0.5)',
            width: 1,
            type: 'dashed',
          },
        },
        axisLabel: {
          fontFamily: 'Arial',
          fontSize: 11,
          formatter: '{value}%',
        },
        // Markers for capacity thresholds
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          animation: true,
          animationDuration: 800,
          data: [
            {
              yAxis: 100,
              lineStyle: {
                color: 'rgba(255, 99, 71, 0.6)',
                width: 2,
                type: 'solid',
                shadowBlur: 4,
                shadowColor: 'rgba(255, 99, 71, 0.3)',
              },
              label: {
                formatter: '100% (Full Capacity)',
                position: 'end',
                fontSize: 12,
                fontFamily: 'Arial',
                padding: [4, 8],
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: 3,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 2,
              },
            },
          ],
        },
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          rotate: 45,
          fontFamily: 'Arial',
          fontSize: 11,
          color: '#555',
          margin: 14,
        },
        axisLine: {
          lineStyle: {
            color: '#ccc',
          },
        },
      },
      animation: true,
      animationDuration: 800,
      animationEasing: 'elasticOut',
    };

    if (this.options.formatter) {
      options.tooltip.formatter = this.options.formatter;
    }

    return options;
  }

  // Helper method for tooltip rows
  _addWorkloadRow(label, value) {
    return `<tr>
      <td class="pr-2">${label}</td>
      <td class="text-right font-bold tabular-nums">${Math.round(value * 100) / 100} pw</td>
    </tr>`;
  }

  get series() {
    const barSeries = {
      type: 'bar',
      id: 'workload',
      encode: {
        x: 'team',
        y: 'percentage',
      },
      barWidth: '50%', // Make bars more substantial
      barCategoryGap: '20%', // More space between bars for cleaner look
      itemStyle: {
        color: (params) => {
          const data = params.data;
          const totalWork = data.total_work;
          const capacityDetails = data.capacity_details;

          if (!capacityDetails) return COLORS.vision;

          const initiativeCapacity = parseFloat(
            capacityDetails.initiative_capacity,
          );
          const totalCapacity = data.total_capacity;

          // Nicer gradient colors based on capacity thresholds
          if (totalWork > totalCapacity) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FF6259' }, // lighter red
                { offset: 1, color: '#E02A1F' }, // darker red
              ],
            };
          } else if (totalWork > initiativeCapacity) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FFA53F' }, // lighter orange
                { offset: 1, color: '#FF7D00' }, // darker orange
              ],
            };
          } else {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#5BEF6A' }, // lighter green
                { offset: 1, color: '#28B637' }, // darker green
              ],
            };
          }
        },
        borderRadius: [5, 5, 0, 0], // Rounded corners at the top
        shadowColor: 'rgba(0, 0, 0, 0.2)',
        shadowBlur: 5,
      },
      label: {
        show: true,
        position: 'top',
        distance: 10,
        formatter: function (params) {
          const data = params.data;
          const totalWork = data.total_work;
          const capacityDetails = data.capacity_details;
          const labelText = data.percentage + '%';

          if (!capacityDetails) return labelText;

          const initiativeCapacity = parseFloat(
            capacityDetails.initiative_capacity,
          );
          const totalCapacity = data.total_capacity;

          // Match label style to the bar color logic
          if (totalWork > totalCapacity) {
            return '{overCapacity|' + labelText + '}';
          } else if (totalWork > initiativeCapacity) {
            return '{flowCapacity|' + labelText + '}';
          } else {
            return '{initiativeCapacity|' + labelText + '}';
          }
        },
        rich: {
          overCapacity: {
            color: '#E02A1F',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(224, 42, 31, 0.3)',
            shadowBlur: 5,
          },
          flowCapacity: {
            color: '#FF7D00',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(255, 125, 0, 0.3)',
            shadowBlur: 5,
          },
          initiativeCapacity: {
            color: '#28B637',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(40, 182, 55, 0.3)',
            shadowBlur: 5,
          },
        },
      },
      markPoint: {
        symbol: 'roundRect',
        symbolSize: [50, 4],
        itemStyle: {
          color: 'rgba(255, 255, 255, 0.85)',
          borderColor: 'rgba(0, 0, 0, 0.3)',
          borderWidth: 1,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
          shadowBlur: 3,
        },
        data: this._getInitiativeCapacityMarkers(),
        silent: true, // Don't trigger events on markers
        animation: true,
        animationDuration: 600,
      },
    };

    return [barSeries];
  }

  // Helper to create initiative capacity markers for each team
  _getInitiativeCapacityMarkers() {
    const markers = [];
    const data = this.dataset[0].source;

    data.forEach((team) => {
      if (!team.capacity_details) return;

      const initiativeCapacity = parseFloat(
        team.capacity_details.initiative_capacity,
      );
      const totalCapacity = team.total_capacity;

      if (totalCapacity > 0) {
        const initiativePercentage = Math.min(
          100,
          (initiativeCapacity / totalCapacity) * 100,
        );

        // Only add threshold if it's visible (above 0% and below 100%)
        if (initiativePercentage > 0 && initiativePercentage < 100) {
          markers.push({
            name: 'Initiative Capacity',
            coord: [team.team, initiativePercentage],
            value: initiativePercentage,
            itemStyle: {
              color: '#000',
            },
            label: {
              show: false,
            },
          });
        }
      }
    });

    return markers;
  }

  get source_buckets_and_uncertainty() {
    let teams_cache = {};
    for (let index = 0; index < this.initiatives?.length; index++) {
      const initiative_id = this.initiatives[index].id;
      const initiative = this.store.peekRecord('initiative', initiative_id);
      let number_of_teams = 1;
      const bon = this.store.peekRecord('bon', initiative_id);
      const investments = bon?.get('investments');
      if (investments == undefined) continue;

      const team_ids_on_bon = investments.calculations
        .map((c) => c.team_id)
        .filter((v) => v !== undefined);
      const unique_team_ids_on_bon = new Set(team_ids_on_bon);
      number_of_teams = unique_team_ids_on_bon.size;
      number_of_teams = number_of_teams > 0 ? number_of_teams : 1;

      // Calculate time overlap factor for this initiative
      const overlapFactor = this.calculateTimeOverlap(initiative, this.roadmap);

      unique_team_ids_on_bon.forEach((team_id) => {
        investments?.calculations?.forEach((calculation) => {
          if (teams_cache[team_id] == undefined) {
            teams_cache[team_id] = {
              VISION: 0,
              'CUSTOMER REQUEST CURRENT': 0,
              'OPERATIONS AND MAINTENANCE': 0,
              UNSPECIFIED: 0,
              UNCERTAINTY: 0,
              initiatives: [],
            };
          }

          if (calculation.team_id == team_id) {
            // Handle undefined or null source
            const source = initiative.source || 'UNSPECIFIED';
            const calculatedWorkload =
              (calculation.people ?? 1) *
              (calculation.weeks ?? 1) *
              (calculation.person_weeks ?? 1) *
              overlapFactor;

            teams_cache[team_id][source] += calculatedWorkload;
            teams_cache[team_id]['UNCERTAINTY'] +=
              (calculation.people ?? 1) *
              (calculation.weeks ?? 1) *
              (calculation.person_weeks ?? 1) *
              (calculation.confidence_value - 1) *
              overlapFactor;

            // Track individual initiative for this team
            const existingInitiative = teams_cache[team_id].initiatives.find(
              (init) => init.id === initiative.id,
            );

            if (existingInitiative) {
              existingInitiative.workload += calculatedWorkload;
            } else {
              teams_cache[team_id].initiatives.push({
                id: initiative.id,
                name: initiative.title,
                workload: calculatedWorkload,
                source: source,
              });
            }
          }
        });
      });
    }

    const source = [];

    for (let [team_id, values] of Object.entries(teams_cache)) {
      const team = this.teams.find((e) => e.id == team_id);
      const name = team?.name ?? 'Unspecified';

      const source_object = {
        team: name,
        vision: values['VISION'],
        customer: values['CUSTOMER REQUEST CURRENT'],
        operations: values['OPERATIONS AND MAINTENANCE'],
        unspecified: values['UNSPECIFIED'],
        uncertainty: values['UNCERTAINTY'],
        initiatives: values['initiatives'] || [],
      };

      if (name == 'Unspecified') {
        source.unshift(source_object);
      } else {
        source.push(source_object);
      }
    }
    return source;
  }

  get source_initiatives_vs_flow() {
    return this.teams.map((team) => {
      const number_of_people = team.ftes ?? team.people.length;

      return {
        team: team.name,
        initiative_capacity: (
          number_of_people *
          this.percentage_initiatives *
          this.weeks
        )?.toFixed?.(1),
        flow_capacity: (
          number_of_people *
          this.percentage_flow *
          this.weeks
        )?.toFixed?.(1),
      };
    });
  }

  get dataset() {
    // Calculate workload percentages per team
    const percentageSource = this.teams
      .map((team) => {
        const teamName = team.name;
        const teamWork = this.source_buckets_and_uncertainty.find(
          (d) => d.team === teamName,
        );
        const teamCapacity = this.source_initiatives_vs_flow.find(
          (d) => d.team === teamName,
        );

        // Calculate total workload (including unspecified and uncertainty)
        const totalWork = teamWork
          ? teamWork.vision +
            teamWork.customer +
            teamWork.operations +
            teamWork.unspecified +
            teamWork.uncertainty
          : 0;

        // Calculate total capacity
        const totalCapacity = teamCapacity
          ? parseFloat(teamCapacity.initiative_capacity) +
            parseFloat(teamCapacity.flow_capacity)
          : 0;

        // Calculate percentage (handle division by zero)
        const percentage =
          totalCapacity > 0 ? (totalWork / totalCapacity) * 100 : 0;

        return {
          team: teamName,
          percentage: percentage.toFixed(1),
          raw_percentage: percentage, // For coloring logic
          // Keep the original data for tooltips
          total_work: totalWork,
          total_capacity: totalCapacity,
          work_details: teamWork,
          capacity_details: teamCapacity,
        };
      })
      .filter((item) => item.raw_percentage > 0); // Only show teams with work

    return [
      {
        dimensions: [
          { name: 'team', displayName: 'Team' },
          { name: 'percentage', displayName: 'Workload %' },
        ],
        source: percentageSource,
      },
    ];
  }

  /*
   * ACCESSOR METHODS
   */

  get teams() {
    return this.options.teams ?? [];
  }

  get percentage_initiatives() {
    return (this.scenario?.allocation?.initiatives?.percent ?? 0) / 100;
  }

  get percentage_flow() {
    return (this.scenario?.allocation?.operational_flex?.percent ?? 0) / 100;
  }

  get weeks() {
    return this.roadmap?.weeks ?? 0;
  }

  calculateTimeOverlap(initiative, roadmap) {
    // Handle missing data
    if (
      !initiative ||
      !roadmap ||
      !initiative.startDate ||
      !initiative.duration
    ) {
      return 1; // Default to full inclusion if data is missing
    }

    // Get initiative date range
    const initiativeStart = initiative.startDate
      ? new Date(initiative.startDate)
      : undefined;
    const initiativeDurationWeeks = parseInt(initiative.duration, 10);

    if (
      !initiativeStart ||
      initiativeStart === '-' ||
      isNaN(initiativeDurationWeeks)
    ) {
      return 1; // Default to full inclusion if dates can't be parsed
    }

    const initiativeEnd = DateTime.from(initiativeStart)
      .add(initiativeDurationWeeks, 'weeks')
      .toDate();

    // Get roadmap date range
    const roadmapStart = new Date(roadmap.start_date);
    const roadmapEnd = new Date(roadmap.end_date);

    // Calculate overlap
    const overlapStart = new Date(
      Math.max(initiativeStart.getTime(), roadmapStart.getTime()),
    );
    const overlapEnd = new Date(
      Math.min(initiativeEnd.getTime(), roadmapEnd.getTime()),
    );

    // No overlap if start is after end
    if (overlapStart >= overlapEnd) {
      return 0;
    }

    // Calculate overlap duration in milliseconds
    const overlapDuration = overlapEnd.getTime() - overlapStart.getTime();
    const initiativeDuration =
      initiativeEnd.getTime() - initiativeStart.getTime();

    // Return ratio of overlap to total initiative duration
    const overlapRatio = overlapDuration / initiativeDuration;

    // Clamp between 0 and 1
    return Math.max(0, Math.min(1, overlapRatio));
  }
}
